import Phaser from 'phaser';
import { COLORS } from '../constants/colors';
import { MOBILE_SHOOT_ZONE_RATIO } from '../constants/hud';
import { getUiScale } from '../constants/scale';

// БАЗОВЫЕ размеры джойстика (на 720). Применяются с множителем uiScale —
// на 360 джойстик и зоны вдвое меньше, не занимают четверть экрана.
const JOYSTICK_RADIUS_BASE = 60;
const KNOB_RADIUS_BASE = 25;
const BASE_DEPTH = 100;
const KNOB_DEPTH = 101;
const BASE_ALPHA = 0.3;
const BASE_STROKE_ALPHA = 0.6;
const KNOB_ALPHA = 0.6;

export class VirtualJoystick {
  private base: Phaser.GameObjects.Arc;
  private knob: Phaser.GameObjects.Arc;
  private pointerId: number | null = null;
  private baseX = 0;
  private baseY = 0;
  private dx = 0;
  private dy = 0;
  // Зафиксированный uiScale на создание — не пересчитывается при resize в бою
  // (resize в GameScene игнорируется, см. этап 8). Стабильно до следующего входа в уровень.
  private readonly uiScale: number;
  private readonly maxDistance: number;

  constructor(private scene: Phaser.Scene) {
    this.uiScale = getUiScale(scene);
    const joystickRadius = JOYSTICK_RADIUS_BASE * this.uiScale;
    const knobRadius = KNOB_RADIUS_BASE * this.uiScale;
    this.maxDistance = joystickRadius - knobRadius;

    this.base = scene.add.circle(0, 0, joystickRadius, COLORS.midGray, BASE_ALPHA);
    this.base.setStrokeStyle(2, COLORS.lightGray, BASE_STROKE_ALPHA);
    this.base.setDepth(BASE_DEPTH);
    this.base.setScrollFactor(0);
    this.base.setVisible(false);

    this.knob = scene.add.circle(0, 0, knobRadius, COLORS.lightGray, KNOB_ALPHA);
    this.knob.setDepth(KNOB_DEPTH);
    this.knob.setScrollFactor(0);
    this.knob.setVisible(false);

    scene.input.on('pointerdown', this.onPointerDown, this);
    scene.input.on('pointermove', this.onPointerMove, this);
    scene.input.on('pointerup', this.onPointerUp, this);
    scene.input.on('pointerupoutside', this.onPointerUp, this);
  }

  private onPointerDown(pointer: Phaser.Input.Pointer): void {
    // Граница 25/75 (стрельба слева, джойстик справа). Раньше 50/50 — мобильным
    // пальцам не хватало места для движения. Нижняя deadzone убрана: слоты
    // оружия защищаются своим object-level pointerdown + stopPropagation,
    // палец на свободном HUD-фоне (не на слоте) считается за движение.
    // Reload-deadzone в правом верхнем тоже убрана — кнопка переехала влево.
    const shootZoneRight = this.scene.scale.width * MOBILE_SHOOT_ZONE_RATIO;
    if (pointer.x >= shootZoneRight && this.pointerId === null) {
      this.pointerId = pointer.id;
      this.baseX = pointer.x;
      this.baseY = pointer.y;

      this.base.setPosition(this.baseX, this.baseY);
      this.base.setVisible(true);

      this.knob.setPosition(this.baseX, this.baseY);
      this.knob.setVisible(true);

      this.dx = 0;
      this.dy = 0;
    }
  }

  private onPointerMove(pointer: Phaser.Input.Pointer): void {
    if (pointer.id === this.pointerId) {
      this.updateKnob(pointer.x, pointer.y);
    }
  }

  private onPointerUp(pointer: Phaser.Input.Pointer): void {
    if (pointer.id === this.pointerId) {
      this.pointerId = null;

      this.base.setVisible(false);
      this.knob.setVisible(false);

      this.dx = 0;
      this.dy = 0;
    }
  }

  private updateKnob(targetX: number, targetY: number): void {
    let dx = targetX - this.baseX;
    let dy = targetY - this.baseY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance > this.maxDistance) {
      dx = (dx / distance) * this.maxDistance;
      dy = (dy / distance) * this.maxDistance;
    }

    this.knob.setPosition(this.baseX + dx, this.baseY + dy);

    this.dx = dx / this.maxDistance;
    this.dy = dy / this.maxDistance;
  }

  getDirection(): { dx: number; dy: number } {
    return { dx: this.dx, dy: this.dy };
  }

  destroy(): void {
    this.base.destroy();
    this.knob.destroy();
    this.scene.input.off('pointerdown', this.onPointerDown, this);
    this.scene.input.off('pointermove', this.onPointerMove, this);
    this.scene.input.off('pointerup', this.onPointerUp, this);
    this.scene.input.off('pointerupoutside', this.onPointerUp, this);
  }
}
